/* eslint-disable react/display-name */
/* eslint-disable no-extra-parens */
import React, { memo, useEffect, useRef } from 'react';
import  isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { dataLayer, utilities } from '@weatherbug/common';

import { reduxConnect } from '../../common';
import { setAppState, getAppState, updateAppState } from '../../common/actions/appStateActions';
import { getQueryParameterByName } from '../../common/utilities/queryParameters';
import { getComplianceRequirementsV3 } from '../../common/actions/legalActions';
import { SimpleStorage } from '../../common/utilities/webStorage';
import { setInitialUnits }  from '../../common/actions/unitsActions';
import { setInitialActiveLocation }  from '../../common/actions/locationActions';

import { switchLocation } from '../../common/actions/locationActions';

import { 
    AppConfigType, 
    RouteParamsType, 
    ComplianceRequirements, 
    FavoriteLocation, 
    IAppState, 
    IStdParameters, 
    IStoreState, 
    Locality, 
    PageMetaData, 
    PageSetup, 
    PlatformKeys, 
    PlatformType, 
    User, 
    WbLocation } from '../../components/common/interfaces/types';
import { generateAppState, isTaboolaAdEnabled, userUpdateChecks } from './appStateUtils';
import { resetServerTrafficCamData } from '../../common/actions/trafficCameraActions';

const { changeLocation } = dataLayer.actions;
const { temperatureUnits, speedUnits, pressureUnits, lengthUnits } = utilities.units;

const newWeatherWidgetPlatformCode = 'wbw';
const oldWeatherWidgetPlatformCode = 'mi';
const weatherWidgetsName = 'weather-widgets';

interface AppStateManagerProps {
    activeRoute: string;
    routePath: string;
    locality: Locality;
    platformCode: PlatformKeys;
    pageId: string;
    pageName: string;
    busDomain: string;
    _User: User;
    config: AppConfigType;
    setAppState: (appState: IAppState, allowStorageAccess: boolean) => void;
    getAppState: () => void;
    updateAppState: (state: IAppState) => void;
    setInitialUnits: typeof setInitialUnits;
    setInitialActiveLocation: typeof setInitialActiveLocation;
    getComplianceRequirementsV3: (baseUrl: string, authId: string, secret: string, stdParameters: IStdParameters, ipAddress: string) => void;
    pageMetaData: PageMetaData;
    routeParams: RouteParamsType;
    complianceRequirements: ComplianceRequirements;
    changeLocation: (latitude: number, longitude: number) => void;
    popupId: string;
    pageSetup: PageSetup;
    switchLocation: (activeLocation: WbLocation) => void;
    favoriteLocations: FavoriteLocation[];
}

type PreviousPropsType = Partial<Pick<AppStateManagerProps, 
    |'activeRoute'
    | 'routeParams' 
    | 'config' 
    | 'busDomain' 
    | 'pageId' 
    | 'pageName' 
    | 'routePath' 
    | 'pageMetaData' 
    | 'pageSetup' 
    | 'locality' 
    | 'popupId' 
    | 'platformCode'
    | '_User' 
    | 'complianceRequirements'>> & {
        enableTaboola?: boolean;
    };

const mapStoreStateToProps = (storeState: IStoreState) => {
    return {
        complianceRequirements: storeState?.legal?.complianceRequirements
    };
};

const mapDispatchToProps = {
    setAppState,
    getAppState,
    updateAppState, 
    getComplianceRequirementsV3,
    changeLocation,
    setInitialUnits,
    setInitialActiveLocation,
    switchLocation,
    resetServerTrafficCamData
};

export const AppStateManager = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    activeRoute,
    routePath,
    locality,
    platformCode,
    pageId,
    pageName,
    busDomain,
    _User,
    config,
    setAppState,
    getAppState,
    updateAppState,
    setInitialUnits,
    setInitialActiveLocation,
    getComplianceRequirementsV3,
    resetServerTrafficCamData,
    pageMetaData,
    routeParams,
    complianceRequirements,
    changeLocation,
    popupId,
    pageSetup,
    switchLocation }: AppStateManagerProps) => {

    const isInitialLoadComplete = useRef<boolean>(false);
    const allowStorageOrCookieAccess = useRef<boolean>(true);
    const storageInstance = useRef<InstanceType<typeof SimpleStorage> | undefined>();
    const prevProps = useRef<PreviousPropsType | undefined>();

    useEffect(() => {
        if (!isInitialLoadComplete.current && _User && config) {
            if ((platformCode?.toLowerCase() === newWeatherWidgetPlatformCode) 
                || (platformCode?.toLowerCase() === oldWeatherWidgetPlatformCode 
                && window?.location?.pathname?.includes(weatherWidgetsName))) {

                allowStorageOrCookieAccess.current = false;
            }
            
            if (allowStorageOrCookieAccess.current) {
                storageInstance.current = new SimpleStorage();  
            }

            let appStateOnMount = generateAppState(
                config, 
                platformCode, 
                _User, 
                activeRoute, 
                complianceRequirements?.disableAds, 
                config?.DisableAds, 
                config?.Platform, 
                allowStorageOrCookieAccess.current);

            if (allowStorageOrCookieAccess.current)
            {
                const ipAddress = getQueryParameterByName('ipAddress');

                const { legalBaseUrl, authId, secret, stdParameters } =  appStateOnMount;
                if (legalBaseUrl && authId && secret) {
                    getComplianceRequirementsV3(legalBaseUrl, authId, secret, stdParameters, ipAddress as string);   
                }
            }

            // eslint-disable-next-line no-inner-declarations
            async function getUserData() {
                const result = await userUpdateChecks(config, _User, storageInstance.current!, allowStorageOrCookieAccess.current);
                return result;
            }

            const { user, appInstanceId } = getUserData() as unknown as { user: User; appInstanceId: string; };

            appStateOnMount.user = {
                ...appStateOnMount.user,
                ...user
            };

            appStateOnMount = {
                ...appStateOnMount,
                appInstanceId: appInstanceId,
                activeRoute,
                routePath,
                pageId,
                pageName,
                busDomain,
                routeParams,
                locality,
                popupId,
                pageFooterSetup: pageSetup?.Footer,
                pageMetaData: pageMetaData
            };
            
            let pressureUnit = Number(_User?.PressureUnit);
            let rainUnit     = Number(_User?.Units);
            let tempUnit     = Number(_User?.Units);
            let windUnit     = Number(_User?.WindUnit);

            // Override the user values if the u query parameter specifies them.
            // TECHDEBT: is the u parameter used?  If so when/where is it used?
            const unitQueryParam = getQueryParameterByName('u');
            if (unitQueryParam) {
                const metricQueryParamValue = 'm';
                tempUnit = unitQueryParam === metricQueryParamValue ? temperatureUnits.celsius : temperatureUnits.fahrenheit;
                pressureUnit = unitQueryParam === metricQueryParamValue ? pressureUnits.millibars : pressureUnits.inches;
                windUnit = unitQueryParam === metricQueryParamValue ? speedUnits.kph : speedUnits.mph;
                rainUnit = unitQueryParam === metricQueryParamValue ? lengthUnits.millimeters : lengthUnits.inches;
            }

            setAppState(appStateOnMount, allowStorageOrCookieAccess.current);
            setInitialUnits(tempUnit, tempUnit, windUnit, pressureUnit, rainUnit);
            setInitialActiveLocation(_User.ActiveLocation);

            prevProps.current = {
                _User: user,
                activeRoute,
                routePath,
                pageId,
                pageName,
                busDomain,
                pageMetaData,
                routeParams,
                locality,
                popupId,
                pageSetup,
                complianceRequirements,
                config
            };
            isInitialLoadComplete.current = true;
        }
    }, [
        platformCode,
        config,
        _User,
        activeRoute,
        routePath,
        pageId,
        pageName,
        busDomain,
        pageMetaData,
        routeParams?.cam,
        routeParams?.camId,
        locality,
        popupId,
        pageSetup,
        complianceRequirements]);
    
    useEffect(() => {
        if (prevProps.current && isInitialLoadComplete.current) {
            let shouldSetAppState = false;
            let shouldUpdateAppState = false;
            let currentProps = cloneDeep(prevProps.current);

            if (routeParams && !isEqual(routeParams, prevProps.current?.routeParams)) {
                currentProps = {
                    ...currentProps,
                    routeParams: {
                        ...routeParams
                    }
                };
                shouldSetAppState = true;
                // Handle routing on Maps page where we don't want full page refresh when changing layers or panning/zooming on map.
                if ((pageId === 'maps' 
                    && ((prevProps.current?.locality?.SlugName === locality?.SlugName) 
                        && (!routeParams?.location || (routeParams?.location && routeParams?.location === prevProps.current?.routeParams?.location))))
                    && ((routeParams?.layerId && routeParams?.layerId !== prevProps.current?.routeParams?.layerId)
                    || (routeParams?.center && routeParams?.center !== prevProps.current?.routeParams?.center))) {
                    shouldUpdateAppState = true;
                }

                // Handle routing in Weather Cams detail page to avoid reloadsh when changing changing cameras.
                if (pageId === 'weather-camera' && routeParams?.cam && routeParams?.cam !== prevProps.current?.routeParams?.cam) {
                    shouldUpdateAppState = true;
                }

                // Handle routing in Traffic Cams detail page to avoid reloads when changing changing cameras.
                if (pageId === 'traffic-camera'&& routeParams?.camId && routeParams?.camId !== prevProps.current?.routeParams?.camId) {
                    shouldUpdateAppState = true;
                }
            }

            if (_User && !isEqual(prevProps.current._User, _User)) {
                currentProps._User = { ..._User };
                shouldSetAppState = true;
            }

            if (platformCode !== prevProps.current.platformCode) {
                currentProps.platformCode = platformCode;
                shouldSetAppState = true;
            }

            if (activeRoute && prevProps.current?.activeRoute !== activeRoute) {
                currentProps.activeRoute = activeRoute;
                shouldSetAppState = true;
            }

            if (routePath && prevProps.current?.routePath !== routePath) {
                currentProps.routePath = routePath;
                shouldSetAppState = true;
            }

            if (pageId && prevProps.current?.pageId !== pageId) {
                currentProps.pageId = pageId;
                shouldSetAppState = true;
            }

            if (pageName && prevProps.current?.pageName !== pageName) {
                currentProps.pageName = pageName;
                shouldSetAppState = true;
            }

            if (busDomain && prevProps.current?.busDomain  !== busDomain) {
                currentProps.busDomain = busDomain;
                shouldSetAppState = true;
            }

            if (pageMetaData?.Canonical && prevProps.current?.pageMetaData?.Canonical !== pageMetaData?.Canonical) {
                currentProps.pageMetaData = pageMetaData;
                shouldSetAppState = true;
            }

            if (locality) {
                if (prevProps.current?.locality?.SlugName !== locality?.SlugName) {
                    currentProps.locality = { ...locality };
                    shouldSetAppState = true;
                    changeLocation(locality.Latitude, locality.Longitude);
                    resetServerTrafficCamData();
                    if (_User.ActiveLocation) {
                        switchLocation(_User.ActiveLocation);                       
                    }

                }
            }

            if (prevProps.current?.popupId !== popupId) {
                currentProps.popupId = popupId;
                shouldSetAppState = true;
            }

            if (pageSetup?.Footer && (prevProps.current?.pageSetup?.Footer !== pageSetup?.Footer)) {
                currentProps.pageSetup = {
                    ...pageSetup,
                    Footer: pageSetup.Footer
                };
                shouldSetAppState = true;
            }

            if (complianceRequirements && Object.keys(complianceRequirements)?.length) {
                const enableTaboola = isTaboolaAdEnabled(complianceRequirements?.disableAds, config?.Taboola?.Disabled, config?.Taboola?.LightHouseQueryParamDisableValue);
                if(enableTaboola !== prevProps.current.enableTaboola) {
                    currentProps.enableTaboola = enableTaboola;
                    shouldSetAppState = true;
                }
            }

            if (currentProps?.config && currentProps?._User && shouldSetAppState) {
                let appStateOnComponentPropsUpdate = generateAppState(
                    currentProps.config, 
                    currentProps.platformCode as PlatformKeys, 
                    currentProps._User, 
                    currentProps.activeRoute as string, 
                    currentProps?.complianceRequirements?.disableAds as boolean, 
                    currentProps.config?.DisableAds as boolean,
                    currentProps.config?.Platform as PlatformType, 
                    allowStorageOrCookieAccess.current);
                
                appStateOnComponentPropsUpdate = {
                    ...appStateOnComponentPropsUpdate,
                    enableTaboola: currentProps.enableTaboola as boolean,
                    activeRoute: currentProps.activeRoute as string,
                    routePath: currentProps.routePath as string,
                    pageId: currentProps.pageId as string,
                    pageName: currentProps.pageName as string,
                    busDomain: currentProps.busDomain as string,
                    routeParams: currentProps.routeParams as RouteParamsType,
                    locality: currentProps.locality as Locality,
                    popupId: currentProps.popupId,
                    pageFooterSetup: currentProps?.pageSetup?.Footer as string,
                    pageMetaData: currentProps.pageMetaData as PageMetaData
                };

                if (shouldUpdateAppState) {
                    updateAppState(appStateOnComponentPropsUpdate);
                } else {
                    setAppState(appStateOnComponentPropsUpdate, allowStorageOrCookieAccess.current);
                }

                prevProps.current = cloneDeep(currentProps);
            }
        }
    }, [
        platformCode,
        config,
        _User,
        activeRoute,
        routePath,
        pageId,
        pageName,
        busDomain,
        pageMetaData,
        routeParams?.cam,
        routeParams?.camId,
        locality,
        popupId,
        pageSetup,
        complianceRequirements]);
    
    return null;
}));